import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useMe } from '@/hooks/useMe';
import { loginRedirectUrl } from '@/api/endpoints';

/**
 * Blocks rendering of its children until `/me` returns 200. On 401, performs
 * a full-page redirect to the backend's `/auth/login`, preserving the
 * current path as `?next=`. Any other error is shown inline so the user
 * isn't stuck in a redirect loop on backend outages.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { data: me, isLoading, error } = useMe();

  useEffect(() => {
    if (isLoading) return;
    if (me === null) {
      const nextPath = `${location.pathname}${location.search}` || '/';
      window.location.href = loginRedirectUrl(nextPath);
    }
  }, [isLoading, me, location.pathname, location.search]);

  if (isLoading || me === null) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          flexDirection: 'column',
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          {me === null ? 'Redirecting to sign in…' : 'Loading…'}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Couldn't reach the server
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error.message}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
}
